/**
 * English strings of the sold services (Sales & finance › Services; ADR-026 §6). The Vietnamese
 * file has exactly the same keys; every state, period, calendar step and staff request the server
 * records has a name here (`tests/subscriptions.test.mjs`).
 */
export const subscriptions = {
  crumb: 'Sales & finance',
  title: 'Services',
  subtitle: 'Every hosting, server, e-mail or other service a customer has bought: when it is due, whether it is paid, and what happened to it.',
  search: 'Domain, host name or customer',
  allStates: 'All states',
  empty: 'No service matches.',
  back: 'Back to services',
  views: { label: 'Service lists', all: 'All', waiting: 'Waiting for delivery', due: 'Due soon', overdue: 'Overdue', days: 'Due within', withinDays: 'Within {{count}} days', limit: 'Showing at most {{count}} rows per group. More records may exist; use All and its filters for the complete list.', paidItems: 'Paid, delivery not yet started', order: 'View order', paidAmount: 'Reserved amount', workflow: 'Delivery processing', openWorkflow: 'View workflow', notStarted: 'Not started' },
  pager: { previous: 'Previous page', next: 'Next page', page: 'Page {{n}}' },
  col: {
    service: 'Service',
    customer: 'Customer',
    plan: 'Plan',
    cycle: 'Cycle',
    state: 'State',
    due: 'Due',
    amount: 'Price per cycle',
  },
  state: {
    PENDING: 'Waiting for payment',
    PROVISIONING: 'Being set up',
    ACTIVE: 'Active',
    OVERDUE: 'Overdue',
    SUSPENDED: 'Suspended',
    CANCELLATION_PENDING: 'Ends at the end of its period',
    TERMINATED: 'Terminated',
    CANCELLED: 'Cancelled',
    FRAUD: 'Frozen (fraud)',
  },
  suspendReason: {
    NON_PAYMENT: 'unpaid',
    ABUSE: 'abuse',
    CUSTOMER_REQUEST: 'at the customer’s request',
    OTHER: 'other reason',
  },
  service: {
    ORDERED: 'Ordered',
    PROVISIONING: 'Being created',
    ACTIVE: 'Running',
    SUSPENDED: 'Suspended at the provider',
    TERMINATED: 'Terminated at the provider',
    FAILED: 'Could not be created',
    UNKNOWN: 'Result not known yet (being checked with the provider)',
  },
  due: {
    none: 'No due date',
    ended: 'Ended',
    later_one: 'In {{count}} day',
    later_other: 'In {{count}} days',
    soon_one: 'In {{count}} day',
    soon_other: 'In {{count}} days',
    today: 'Due today',
    overdue_one: '{{count}} day overdue',
    overdue_other: '{{count}} days overdue',
  },
  cycle: {
    ONCE: 'One-time',
    H1: '1 hour',
    M1: '1 month',
    M3: '3 months',
    M6: '6 months',
    M12: '1 year',
    M24: '2 years',
    M36: '3 years',
    M48: '4 years',
    M60: '5 years',
  },
  fulfilment: { PROVISIONED: 'Set up automatically', MANUAL: 'Set up by staff' },
  autoRenew: { on: 'Pays from the wallet when it can', off: 'Not renewed automatically' },
  detail: {
    overview: 'Overview',
    customer: 'Customer',
    product: 'Product',
    plan: 'Plan',
    cycle: 'Cycle',
    state: 'State',
    nextDue: 'Paid until',
    anchor: 'Started on',
    amount: 'Price per cycle (before VAT)',
    autoRenew: 'Renewal',
    delivery: 'Delivery',
    service: 'At the provider',
    paidUntil: 'Paid until at the provider',
    hold: 'No suspension before',
    lifecycle: 'Overdue settings in force',
    lifecycleText: 'Renewal order {{invoice}} days before due · suspended {{suspend}} days after due · terminated {{terminate}} days after due',
    lifecycleOff: 'Never suspended or terminated automatically',
    periods: 'Paid periods',
    renewals: 'Renewal orders',
    calendar: 'Overdue calendar',
    history: 'History',
    actions: 'Staff requests',
    none: 'None yet.',
    qrCode: 'Transfer code {{code}}',
    operations: 'Provisioning log',
    operationsHint: 'What was sent to the provider for this service, newest first (Vietnam time).',
    orders: 'Orders & invoices',
    cancellations: 'Cancellations',
  },
  delivery: {
    col: 'Delivery status',
    none: 'Nothing sent to the provider yet',
    workflow: 'Delivery stopped ({{code}})',
    attempts_one: '{{count}} attempt',
    attempts_other: '{{count}} attempts',
  },
  opName: {
    create: 'Create the account',
    suspend: 'Suspend',
    unsuspend: 'Unsuspend',
    terminate: 'Terminate',
    change_plan: 'Change plan',
    renew: 'Renew',
    reset_password: 'Set the password',
    ssl_auto: 'AutoSSL',
    ssl_install: 'Install a certificate',
    domain_add: 'Add a domain',
    domain_remove: 'Remove a domain',
    db_create: 'Create a database',
    db_delete: 'Delete a database',
    ftp_create: 'Create an FTP account',
    ftp_delete: 'Delete an FTP account',
    backup_create: 'Back up',
    backup_restore: 'Restore a backup',
    apply: 'Apply the DNS records',
    repair: 'Repair the DNS differences',
    dnssec_enable: 'Turn DNSSEC on',
    dnssec_disable: 'Turn DNSSEC off',
    allow_transfer: 'Allow zone transfers',
    native_template: "Apply the DNS server's template",
  },
  opStatus: {
    QUEUED: 'Waiting to be sent',
    SENDING: 'Sending',
    RUNNING: 'Running at the provider',
    SUCCEEDED: 'Done',
    REFUSED: 'Refused by the provider',
    FAILED: 'Failed',
    UNKNOWN: 'Result unknown',
    CANCELLED: 'Cancelled',
  },
  opCol: { op: 'Operation', status: 'Status', attempts: 'Attempts', detail: 'Detail', at: 'Created', finished: 'Finished', action: 'Action' },
  orderCol: { order: 'Order', action: 'Kind', status: 'Status', amount: 'Amount', invoice: 'Invoice', at: 'At' },
  orderAction: { NEW: 'New service', RENEW: 'Renewal', CHANGE: 'Change' },
  cancelCol: { timing: 'When', state: 'State', reason: 'Reason', at: 'Asked at', done: 'Done at' },
  cancelState: { REQUESTED: 'Requested', ACCEPTED: 'Accepted', REJECTED: 'Rejected', WITHDRAWN: 'Withdrawn', DONE: 'Done' },
  hosting: {
    title: 'Technical (cPanel)',
    subtitle: 'The hosting account as our records hold it; "Sync from server" reads it at the panel again.',
    loadFailed: 'The technical side of the service could not be read.',
    username: 'cPanel username',
    domain: 'Main domain',
    server: 'Server',
    ip: 'IP address',
    nameservers: 'Name servers',
    connection: 'Connection',
    kernel: 'Our state',
    panel: 'At the panel',
    checked: 'Last read from the panel',
    drift: 'Mismatch with the panel',
    driftNone: 'None',
    driftMissing: 'The account is not on the panel any more',
    driftStates: 'Here: {{ours}} · At the panel: {{theirs}}',
    attention: 'Needs staff',
    attentionHint: 'A provisioning operation has an unknown outcome or was refused: see the provisioning log below.',
    created: 'Created on the panel',
    released: 'Released',
    notYet: 'Not yet',
    shared: 'Shared address',
    dedicated: 'Dedicated address',
    usage: 'Usage',
    usageNone: 'No usage recorded yet (read from the panel every hour).',
    meter: { DISK_MB: 'Disk', INODES: 'Files (inodes)', BANDWIDTH_MB: 'Bandwidth this month' },
    unlimited: 'no limit',
    panelStatus: { PROVISIONING: 'Being created', ACTIVE: 'Running', SUSPENDED: 'Suspended', TERMINATED: 'Removed', FAILED: 'Failed', UNKNOWN: 'Unknown' },
    signIn: 'Sign in to cPanel',
    signInTitle: 'Sign in to cPanel as {{username}}',
    signInIntro: 'Makes a one-time link into the customer\'s cPanel (valid a few minutes). The link is never stored; the sign-in is audited with your reason.',
    signInReady: 'The link is ready: it works once and expires at {{at}}.',
    signInOpen: 'Open cPanel',
    password: 'Set cPanel password',
    passwordTitle: 'New cPanel password for {{username}}',
    passwordIntro: 'Sets the password of the cPanel account at once. It is sent to the panel only and never stored: give it to the customer through a safe channel.',
    passwordField: 'New password',
    passwordHint: 'At least {{min}} characters, two kinds of characters (lower case, upper case, digits, symbols), without the username.',
    passwordDone: 'The password was set on the panel.',
    refresh: 'Sync from server',
    refreshTitle: 'Read {{username}} at the panel',
    refreshIntro: 'Reads the account at the panel now: its state, server, IP address and name servers (after a change made by hand on the server).',
    refreshDone: 'Read from the panel.',
    autossl: 'Run AutoSSL',
    autosslTitle: 'AutoSSL for {{domain}}',
    autosslIntro: 'Asks the panel to issue or renew its free certificates for the account\'s domains. The request is queued and sent within a minute; its result shows in the provisioning log.',
    autosslDone: 'AutoSSL queued.',
    resolve: 'Resolve',
    resolveTitle: 'Resolve "{{op}}" (result unknown)',
    resolveIntro: 'The panel did not answer clearly. Look at the account on the server (WHM › List Accounts) and say what happened.',
    resolveChoice: 'What happened on the server',
    resolution: {
      DONE: 'It took effect on the server',
      NOT_DONE: 'It did not take effect: send it again',
      ABANDON: 'Give it up (a create fails the service and the funds go back to the wallet)',
    },
    providerRef: 'cPanel username on the server',
    providerRefHint: 'Empty: {{username}}. Another name is checked against the panel\'s rules, then recorded as the account\'s.',
    resolveDone: 'Recorded. The service follows within a few minutes.',
  },
  periodCol: { kind: 'Kind', from: 'From', to: 'To', amount: 'Amount', reason: 'Reason' },
  period: { FIRST: 'First period', RENEWAL: 'Renewal', CHANGE: 'Plan change', EXTENSION: 'Free extension', TRIAL: 'Trial' },
  renewalCol: { due: 'For the due date', state: 'State', paidBy: 'Paid by', amount: 'Amount (VAT incl.)', payment: 'Payment' },
  renewal: { OPEN: 'Waiting for payment', PAID: 'Paid', CANCELLED: 'Cancelled', LAPSED: 'Lapsed' },
  paidBy: { WALLET: 'Wallet', TRANSFER: 'Bank transfer (QR)' },
  calendarCol: { due: 'Due date', step: 'Step', day: 'Day', at: 'Done at' },
  step: {
    RENEWAL_ORDER: 'Renewal order issued',
    REMINDER: 'Reminder sent',
    OVERDUE: 'Became overdue',
    UNPAID_SIGNAL: 'Suspension scheduled at the provider',
    LATE_FEE: 'Late fee added',
    SUSPENDED: 'Suspended',
    TERMINATED: 'Terminated',
    TERMINATION_NOTICE: 'Termination notice sent',
  },
  dayBefore_one: '{{count}} day before due',
  dayBefore_other: '{{count}} days before due',
  dayAfter_one: '{{count}} day after due',
  dayAfter_other: '{{count}} days after due',
  dayDue: 'On the due date',
  historyCol: { from: 'From', to: 'To', reason: 'Reason', at: 'At' },
  reason: {
    SERVICE_PROVISIONED: 'The service was set up',
    UNPAID_AT_DUE: 'Not paid by the due date',
    NON_PAYMENT: 'Not paid',
    PAID: 'Paid',
    EXTENDED: 'Extended by staff',
    CANCELLED_AT_PERIOD_END: 'Ended at the end of its period',
    ORDER_PAID: 'The order was paid',
  },
  actionCol: { kind: 'Request', reason: 'Reason', state: 'State', at: 'Asked at' },
  action: { SUSPEND: 'Suspend', UNSUSPEND: 'Unsuspend', BILLING_PAID: 'Tell the provider it is paid', TERMINATE: 'Terminate' },
  actionState: { PENDING: 'Waiting', DONE: 'Done', FAILED: 'Failed' },
  op: {
    completeDelivery: 'Complete delivery',
    completeDeliveryTitle: 'Confirm delivery of {{label}}',
    completeDeliveryIntro: 'Confirm only after the service has been provisioned and handed to the customer. This activates the service, starts its first period, captures reserved funds and creates the invoice.',
    deliveryDone: 'Delivery confirmed and service activated. The first period, capture and invoice have been recorded.',
    cancelDelivery: 'Cannot deliver',
    cancelDeliveryTitle: 'Cannot deliver {{label}}',
    cancelDeliveryIntro: 'Use when the service cannot be delivered to the customer. The service is cancelled and the reserved funds go back to the customer\'s wallet; nothing is captured or invoiced. This cannot be undone.',
    deliveryCancelled: 'The undeliverable service was cancelled. The reserved funds went back to the customer\'s wallet.',

    extend: 'Extend',
    extendTitle: 'Extend {{label}} without charge',
    extendIntro: 'The due date moves later by the number of days given; nothing is charged. Above {{days}} days a second permission is needed.',
    days: 'Days',
    suspend: 'Suspend',
    suspendTitle: 'Suspend {{label}}',
    suspendIntro: 'The service stops at the provider. A suspension by staff is never lifted by a payment.',
    suspendReason: 'Why',
    unsuspend: 'Unsuspend',
    unsuspendTitle: 'Unsuspend {{label}}',
    unsuspendIntro: 'The service runs again at the provider.',
    terminate: 'Terminate',
    terminateTitle: 'Terminate {{label}}',
    terminateIntro: 'At the end of its period the service is not renewed and ends on its due date; at once, it is terminated now and its data at the provider is deleted.',
    timing: 'When',
    timingOption: { END_OF_PERIOD: 'At the end of the period', IMMEDIATE: 'At once' },
    done: 'Request recorded. The provider may not have completed it yet; check the provider state to confirm.',
    submit: 'Confirm',
  },
};
